import { http } from '@/app/lib/http/client'

export type CatalogAction = 'INCLUDE' | 'EXCLUDE'
export type CatalogRuleType = 'PRODUCT' | 'CATEGORY' | 'DEPARTMENT' | 'PRODUCT_TYPE'
export type CatalogOptionType = 'sucursals' | 'categories' | 'departments' | 'product-types'

export interface CatalogPage<T> {
  items: T[]
  pagination: { current_page: number; per_page: number; last_page: number; total: number }
}

export interface CatalogChannel {
  id: number
  code: string
  name: string
  default_action: CatalogAction
  is_active: boolean
  rule_counts: Record<CatalogRuleType, number>
}

export interface CatalogProduct {
  id: number
  slug: string | null
  name: string
  sku: string | null
  category: { id: number; name: string } | null
  department: { id: number; name: string } | null
  product_type: { id: number; name: string } | null
  publication: {
    is_published: boolean
    action: CatalogAction
    source_type: CatalogRuleType | 'DEFAULT'
    rule_id: number | null
  }
}

export interface CatalogRule {
  id: number
  channel_id: number
  type: CatalogRuleType
  reference_id: number
  reference_name: string | null
  action: CatalogAction
}

export interface CatalogOption {
  id: number
  name: string
  code?: string
  slug?: string
}

export interface CatalogProductFilters {
  search?: string
  category_id?: number
  department_id?: number
  product_type_id?: number
  published?: boolean
  page?: number
  per_page?: number
}

export interface CatalogRuleFilters {
  search?: string
  type?: CatalogRuleType
  action?: CatalogAction
  page?: number
  per_page?: number
}

export interface CatalogRuleKey {
  type: CatalogRuleType
  reference_id: number
}

export const onlineCatalogService = {
  async channel(): Promise<CatalogChannel> {
    const response = await http.get('/ecommerce/catalog/channel')
    return response.data.data
  },

  async updateChannel(data: { name: string; default_action: CatalogAction }): Promise<CatalogChannel> {
    const response = await http.put('/ecommerce/catalog/channel', data)
    return response.data.data
  },

  async updateStatus(is_active: boolean): Promise<CatalogChannel> {
    const response = await http.patch('/ecommerce/catalog/channel/status', { is_active })
    return response.data.data
  },

  async products(params: CatalogProductFilters): Promise<CatalogPage<CatalogProduct>> {
    const response = await http.get('/ecommerce/catalog/products', { params })
    return response.data.data
  },

  async rules(params: CatalogRuleFilters): Promise<CatalogPage<CatalogRule>> {
    const response = await http.get('/ecommerce/catalog/rules', { params })
    return response.data.data
  },

  async syncRules(data: {
    upsert?: Array<CatalogRuleKey & { action: CatalogAction }>
    remove?: CatalogRuleKey[]
  }): Promise<{ upserted: number; removed: number; total: number }> {
    const response = await http.patch('/ecommerce/catalog/rules', data)
    return response.data.data
  },

  async options(type: CatalogOptionType, params: { search?: string; page?: number; per_page?: number }): Promise<CatalogPage<CatalogOption>> {
    const response = await http.get(`/ecommerce/catalog/options/${type}`, { params })
    return response.data.data
  },
}
